import React, { useContext, useEffect, useState } from "react";
import ReactDOM from "react-dom";

import Tippy from "@tippyjs/react";
import cx from "classnames";
import EventEmitter from "events";
import { cloneDeep } from "lodash/fp";
import { EditorState, Plugin, PluginKey } from "prosemirror-state";
import { commitLocalUpdate, fetchQuery, graphql } from "relay-runtime";

import { Views } from "~/actionItems/constants";
import { ActionItemsContext } from "~/actionItems/newActionItems/ActionItemsContext";
import { ReactComponent as AsanaLogo } from "~/legacy-assets/images/logos/asana.svg";
import { inCalendarMode, inVideocallMode, isMobile } from "~/lib/utils";
import { environment } from "~/models";

import { Extension } from "../extension";
import { addAnnotation, ASANA_ANNOTATION_ORDER, removeAnnotation } from "./blockAnnotation";
import { getBlockBoundaryState } from "./blockBoundary";

import type { asanaItemsAsanaBlockLinkNode$data } from "~/__generated__/asanaItemsAsanaBlockLinkNode.graphql";
import type {
	asanaItemsSingleAsanaBlockLinkQuery,
	asanaItemsSingleAsanaBlockLinkQuery$data,
} from "~/__generated__/asanaItemsSingleAsanaBlockLinkQuery.graphql";

import styles from "~/integrations/actionItemIntegration/css/pill.module.css";

export const proseIntegrationKey = "asana-items";
export const asanaRefetchMetaKey = "asanaRefetched";
export const AsanaItemsPluginKey = new PluginKey<AsanaItemsState>(proseIntegrationKey);

export type AsanaItemDataFromQuery = asanaItemsSingleAsanaBlockLinkQuery$data["node"];

export type AsanaItemData = {
	blockId: string;
	blockKey: string;
	asanablocklink: {
		id: string;
		project: {
			id: string;
			name: string;
			externalId: string | null;
		};
		externalId: string | null;
	} | null;
	progressState: AsanaUpdateType;
};

export type AsanaItemsData = {
	[block: string]: AsanaItemData;
};

type InitialAsanaItemsData = readonly {
	blockKey: string;
	blockId: string;
	asanablocklink: {
		id: string;
		project: {
			id: string;
			name: string;
			externalId: string | null;
		};
		externalId: string | null;
	} | null;
}[];

export type AsanaItemsUpdateData = {
	updateType: AsanaUpdateType;
	blockId: string;
	blockKey: string;
	projectId: string;
	projectName: string;
	asanaProjectId: string;
	asanaBlockId: string;
	asanaBlockLinkId: string;
};

export type AsanaUpdateType = "ASANA_UPDATED" | "ASANA_COMPLETED" | "ASANA_ITEM_UNLINKED";

type AsanaItemsState = {
	itemsData: AsanaItemsData;
};

graphql`
	fragment asanaItemsAsanaBlockLinkNode on AsanaBlockLinkNode {
		id
		project {
			id
			name
			externalId
		}
		externalId
	}
`;

graphql`
	fragment asanaItemsNoteBlock on NoteBlock {
		blockId: id
		blockKey: key
		asanablocklink {
			...asanaItemsAsanaBlockLinkNode @relay(mask: false)
		}
	}
`;

graphql`
	fragment asanaItems on Note {
		asanaActionItems: actionItems(asanaOnly: true) {
			...asanaItemsNoteBlock @relay(mask: false)
		}
	}
`;

const singleAsanaBlockLinkQuery = graphql`
	query asanaItemsSingleAsanaBlockLinkQuery($id: ID!) {
		node(id: $id) {
			...asanaItemsNoteBlock @relay(mask: false)
		}
	}
`;

function fetchSingleAsanaBlockLink(id: string) {
	return fetchQuery<asanaItemsSingleAsanaBlockLinkQuery>(environment, singleAsanaBlockLinkQuery, { id }).toPromise();
}

export function constructAsanaDataForWidget(
	asanaBlockLink: Omit<asanaItemsAsanaBlockLinkNode$data, " $fragmentType">,
	blockId: string,
	blockKey: string,
	progressState: AsanaUpdateType,
): AsanaItemData {
	return { blockId: blockId, blockKey: blockKey, asanablocklink: asanaBlockLink, progressState: progressState };
}

function convertAsanaItemDataFromQueryToAsanaItemData(
	fromQuery: AsanaItemDataFromQuery | null,
	progressState: AsanaUpdateType,
): AsanaItemData | null {
	if (!fromQuery) return null;

	const { blockId, blockKey, asanablocklink } = fromQuery;
	const { id: asanaBlockLinkId, project, externalId: asanaBlockLinkExternalId } = asanablocklink ?? {};
	const { id: projectId, name, externalId: externalProjectId } = project ?? {};

	return {
		blockId: blockId ?? "",
		blockKey: blockKey ?? "",
		asanablocklink: {
			id: asanaBlockLinkId ?? "",
			project: {
				id: projectId ?? "",
				name: name ?? "",
				externalId: externalProjectId ?? "",
			},
			externalId: asanaBlockLinkExternalId ?? "",
		},
		progressState: progressState,
	};
}

export default class AsanaItems extends Extension {
	name = "asana-items";
	noteId: string;
	initialData: AsanaItemsData;
	asanaWidgetNodes = new Map<string, Element>();
	blockDataEmitters = new Map<string, EventEmitter>();

	constructor({ noteId, initialData }: { noteId: string; initialData: InitialAsanaItemsData }) {
		super();

		this.noteId = noteId;
		this.initialData = {};

		for (const { blockKey, blockId, asanablocklink: asanaBlockLink } of initialData) {
			if (!this.initialData[blockKey] && asanaBlockLink) {
				this.initialData[blockKey] = {
					blockId: blockId,
					blockKey: blockKey,
					asanablocklink: {
						id: asanaBlockLink.id,
						project: asanaBlockLink.project,
						externalId: asanaBlockLink.externalId,
					},
					progressState: "ASANA_COMPLETED",
				};
			}
		}
	}

	doAsanaBlockLinkUpdate(updateData: AsanaItemDataFromQuery, updateType: AsanaUpdateType) {
		if (!updateData) return;
		if (updateType === "ASANA_ITEM_UNLINKED") {
			this.deleteFromRelayStorage(updateData);
		} else {
			if (!updateData?.blockKey) return;
			this.updateRelayStorage(updateData, updateData.blockKey);
		}
		this.editorView?.dispatch(
			this.editorView.state.tr.setMeta(
				AsanaItemsPluginKey,
				convertAsanaItemDataFromQueryToAsanaItemData(updateData, updateType),
			),
		);
	}

	async dispatchAsanaUpdate(updateData: AsanaItemsUpdateData) {
		const asanaBlockLinkData = await fetchSingleAsanaBlockLink(updateData.blockId);
		if (!asanaBlockLinkData) return;

		let { node } = asanaBlockLinkData;
		if (!node) return;

		let asanaBlockLinkNode = node;

		// If asanablocklink isn't set yet, perform an optimistic update of the data for the widget
		// using what was passed in the WS signal.
		// This will happen in the initial sync, before the asanablocklink has been created and the task is created in Asana

		// If there is no asanablocklink and the signal is "ASANA_ITEM_UNLINKED" it means the initial sync was canceled,
		// likely due to reassigning the action item when only a user-level integration is active.
		// The "unlinked" signal isn't entirely accurate, there was never a link to unlink in the first place.

		// TODO: (Asana big refactor) treat this the same as the other integrations and just perform a db query
		if (!asanaBlockLinkNode.asanablocklink && updateData.updateType !== "ASANA_ITEM_UNLINKED") {
			asanaBlockLinkNode = {
				blockId: node.blockId,
				blockKey: node.blockKey,
				asanablocklink: {
					id: updateData.asanaBlockLinkId,
					project: {
						id: updateData.projectId,
						name: updateData.projectName,
						externalId: updateData.asanaProjectId,
					},
					externalId: updateData.asanaBlockId,
				},
			};
		}

		this.doAsanaBlockLinkUpdate(asanaBlockLinkNode, updateData.updateType);
	}

	applyUpdateData = (oldData: AsanaItemsData, update: AsanaItemData): AsanaItemsData => {
		const { blockKey } = update;
		const newData = cloneDeep(oldData);

		if (update.progressState === "ASANA_ITEM_UNLINKED") {
			const widgetDom = this.asanaWidgetNodes.get(blockKey);
			if (widgetDom) {
				ReactDOM.unmountComponentAtNode(widgetDom);
				if (this.editorView) removeAnnotation(this.editorView, blockKey, widgetDom);
				widgetDom.remove();
			}
			this.asanaWidgetNodes.delete(blockKey);
			this.blockDataEmitters.delete(blockKey);
			delete newData[blockKey];
			return newData;
		} else {
			// In this version, we aren't updating the project, only the status
			this.blockDataEmitters.get(blockKey)?.emit("data", update);
			newData[blockKey] = update;
		}

		return newData;
	};

	refreshAsanaItemsData = (dataFromQuery: InitialAsanaItemsData) => {
		const view = this.editorView;
		if (!view) return;
		const newState: AsanaItemsData = {};

		for (const { blockId, blockKey, asanablocklink } of dataFromQuery) {
			if (!newState[blockKey] && asanablocklink) {
				const newAsanaItemData = {
					blockId: blockId,
					blockKey: blockKey,
					asanablocklink: asanablocklink,
					progressState: "ASANA_COMPLETED" as AsanaUpdateType,
				};
				newState[blockKey] = newAsanaItemData;
			}
		}

		view.dispatch(view.state.tr.setMeta(asanaRefetchMetaKey, newState));

		this.blockDataEmitters.forEach((emitter, k) => {
			const newAsanaItemData = newState[k];
			if (newAsanaItemData) {
				emitter.emit("data", newAsanaItemData);
			}
		});
	};

	updateRelayStorage = (update: AsanaItemDataFromQuery, blockKey: string) => {
		if (!update) return;
		commitLocalUpdate(environment, store => {
			if (update.blockId) {
				const noteId = this.noteId;
				const noteNode = store.get(noteId);
				const actionItems = noteNode?.getLinkedRecords("actionItems", { asanaOnly: true })?.slice() ?? [];

				const { asanablocklink } = update;
				if (asanablocklink) {
					const asanaBlockLinkId = asanablocklink.id;
					const projectId = asanablocklink.project.id;
					const projectName = asanablocklink.project.name;
					const asanaProjectId = asanablocklink.project.externalId;
					const asanaBlockId = asanablocklink.externalId;

					if (asanaBlockLinkId && !store.get(asanaBlockLinkId)) {
						const newBlockLink = store.create(asanaBlockLinkId, "AsanaBlockLinkNode");
						newBlockLink.setValue(asanaBlockLinkId, "id");
						newBlockLink.setValue(asanaBlockId, "externalId");

						const asanaProjectNode = store.get(projectId);
						if (!asanaProjectNode) {
							const newProject = store.create(projectId, "AsanaProjectNode");
							newProject.setValue(projectId, "id");
							newProject.setValue(projectName, "name");
							newProject.setValue(asanaProjectId, "externalId");
							newBlockLink.setLinkedRecord(newProject, "project");
						}

						let block = store.get(update.blockId);
						if (!block) {
							block = store.create(update.blockId, "NoteBlock");
							block.setValue(blockKey, "key");
						}
						block.setLinkedRecord(newBlockLink, "asanablocklink");

						actionItems.push(block);
						noteNode?.setLinkedRecords(actionItems, "actionItems", { asanaOnly: true });
					}
				}
			}
		});
	};

	deleteFromRelayStorage = (update: AsanaItemDataFromQuery) => {
		if (!update) return;
		commitLocalUpdate(environment, store => {
			if (update.blockId) {
				const { asanablocklink } = update;

				if (asanablocklink) {
					const asanaBlockLinkId = asanablocklink.id;
					const projectId = asanablocklink.project.id;
					const asanaProjectId = asanablocklink.project.externalId;
					const asanaBlockId = asanablocklink.externalId;
					store.delete(asanaBlockLinkId);
					store.delete(projectId);
					if (asanaBlockId) store.delete(asanaBlockId);
					if (asanaProjectId) store.delete(asanaProjectId);
				}
			}
		});
	};

	updateWidgets = (newState: EditorState, currentPluginState: AsanaItemsState) => {
		const editorView = this.editorView;
		if (!editorView) return;

		const blockBoundaryState = getBlockBoundaryState(newState);
		if (!blockBoundaryState) return;
		const { blockBoundaries } = blockBoundaryState;

		const blockKeysWithLinksRemoved = new Set(
			[...this.asanaWidgetNodes.keys()].filter(k => !(k in currentPluginState.itemsData)),
		);
		blockKeysWithLinksRemoved.forEach(k => {
			let dom = this.asanaWidgetNodes.get(k);
			if (dom) {
				ReactDOM.unmountComponentAtNode(dom);
				removeAnnotation(editorView, k, dom);
				dom.remove();
			}
			this.asanaWidgetNodes.delete(k);
			this.blockDataEmitters.delete(k);
		});

		Object.entries(currentPluginState.itemsData).forEach(([k, v]) => {
			if (!(k in blockBoundaries)) return;

			let dom = this.asanaWidgetNodes.get(k);
			if (!dom) {
				const div = document.createElement("div");
				div.contentEditable = "false";
				dom = div;
				this.asanaWidgetNodes.set(k, dom);
				let emitter = this.blockDataEmitters.get(k);
				if (!emitter) {
					emitter = new EventEmitter();
					this.blockDataEmitters.set(k, emitter);
				}
				ReactDOM.render(<AsanaItemsWidget asanaItem={v} asanaEmitter={emitter} />, dom);
				emitter.emit("data", v);
				addAnnotation(editorView, k, dom, ASANA_ANNOTATION_ORDER);
			}
		});
	};

	get plugins() {
		return [
			new Plugin<AsanaItemsState>({
				key: AsanaItemsPluginKey,
				props: {},
				state: {
					init: (_config, state: EditorState) => {
						const value = { itemsData: this.initialData };
						this.updateWidgets(state, value);
						return value;
					},
					apply: (tr, value, _oldState, newState) => {
						if (tr.getMeta(AsanaItemsPluginKey)) {
							const newData: AsanaItemData = tr.getMeta(AsanaItemsPluginKey);
							value.itemsData = this.applyUpdateData(value.itemsData, newData);
						} else if (tr.getMeta(asanaRefetchMetaKey)) {
							value.itemsData = tr.getMeta(asanaRefetchMetaKey);
						}
						this.updateWidgets(newState, value);
						return value;
					},
				},
			}),
		];
	}

	destroy = () => {
		this.asanaWidgetNodes.forEach(v => {
			ReactDOM.unmountComponentAtNode(v);
			v.remove();
		});
	};
}

export const clickAsanaLink = (projectExternalId?: string | null, itemExternalId?: string | null) => {
	const baseUrl = `https://app.asana.com/0/${projectExternalId}`;

	if (projectExternalId) {
		if (itemExternalId) window.open(`${baseUrl}/${itemExternalId}`, "_blank", "noopener");
		else window.open(baseUrl, "_blank", "noopener");
	} else console.error("Unable to open Asana link");
};

export function AsanaItemsWidget({
	asanaItem: initialAsanaItem,
	asanaEmitter,
	customClassName,
}: {
	asanaItem: AsanaItemsData[string];
	asanaEmitter?: EventEmitter;
	customClassName?: string;
}) {
	const [asanaItem, setAsanaItem] = useState<AsanaItemsData[string] | null>(initialAsanaItem);
	const [progressState, setProgressState] = useState<AsanaUpdateType>();

	useEffect(() => {
		const listener = (data: null | AsanaItemData) => {
			if (!data) return;
			setProgressState(data.progressState);
			setAsanaItem(data);
		};
		asanaEmitter?.addListener("data", listener);
		return () => {
			asanaEmitter?.removeListener("data", listener);
		};
	}, [setAsanaItem, asanaEmitter]);

	const onClickWidget = React.useCallback(() => {
		clickAsanaLink(asanaItem?.asanablocklink?.project?.externalId, asanaItem?.asanablocklink?.externalId);
	}, [asanaItem?.asanablocklink?.externalId, asanaItem?.asanablocklink?.project?.externalId]);

	const projectExists = React.useMemo(() => !!(asanaItem && asanaItem.asanablocklink?.project), [asanaItem]);

	const { view } = useContext(ActionItemsContext);
	const smallPill = (isMobile || inVideocallMode || inCalendarMode) && view === Views.STREAM_TAB;

	return projectExists ? (
		<Tippy content="Syncing to Asana" className={styles.toolTip} disabled={progressState !== "ASANA_UPDATED"}>
			<button onClick={onClickWidget} className={cx(styles.pill, customClassName)}>
				<div className={cx({ [styles.greyscale]: progressState == "ASANA_UPDATED" })}>
					<AsanaLogo />
				</div>
				{!smallPill && (
					<div className={styles.projectSpecific}>
						<div className={styles.projectName}>{asanaItem?.asanablocklink?.project.name}</div>
					</div>
				)}
			</button>
		</Tippy>
	) : (
		<></>
	);
}
